import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import { apiService } from '../../api/apiService';
import { useAuth } from '../../contexts/AuthContext';
import type { Training } from '../../types';
import { Card } from '../../components/ui/Card';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import 'react-calendar/dist/Calendar.css';
import '../user/UserCalendar.css';

export const CoachCalendar: React.FC = () => {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date());
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [selectedDateTrainings, setSelectedDateTrainings] = useState<Training[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    loadTrainings();
  }, []);

  useEffect(() => {
    filterSelectedDateTrainings();
  }, [trainings, date]);

  const loadTrainings = async () => {
    try {
      // Use the appropriate endpoint based on user role
      const data = user?.role === 'coach' || user?.role === 'admin'
        ? await apiService.getCoachTrainings()
        : await apiService.getTrainings();
      // Sort trainings by date (most recent first)
      setTrainings(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterSelectedDateTrainings = () => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filtered = trainings.filter((training) => {
      const trainingDate = formatTrainingDate(training.date);
      return trainingDate === dateStr;
    });
    // Sort filtered trainings by date (most recent first)
    const sorted = filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setSelectedDateTrainings(sorted);
  };

  // Convert ISO timestamp to yyyy-MM-dd format
  const formatTrainingDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString;
    }
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const hasTraining = trainings.some((training) => {
      const trainingDate = formatTrainingDate(training.date);
      return trainingDate === dateStr;
    });
    return hasTraining ? 'has-training' : '';
  };

  const tileContent = ({ date }: { date: Date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayTrainings = trainings.filter((training) => {
      const trainingDate = formatTrainingDate(training.date);
      return trainingDate === dateStr;
    });

    if (dayTrainings.length === 0) return null;

    // Si hay más de 3 entrenamientos, mostrar solo el número
    if (dayTrainings.length > 3) {
      return (
        <div className="tile-trainings">
          <div className="tile-training-count">
            {dayTrainings.length} {t('trainings').toLowerCase()}
          </div>
        </div>
      );
    }

    return (
      <div className="tile-trainings">
        {dayTrainings.map((training) => (
          <div
            key={training.id}
            className="tile-training-item"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/training/${training.id}`);
            }}
          >
            {training.title}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="user-calendar-page">
      <h1 className="page-title">{t('calendar')}</h1>

      <div className="calendar-container">
        <Card>
          <Calendar
            onChange={(value) => setDate(value as Date)}
            value={date}
            locale={i18n.language === 'es' ? 'es' : 'en'}
            tileClassName={tileClassName}
            tileContent={tileContent}
          />
        </Card>

        <div className="trainings-list">
          <h2 className="trainings-title">
            {format(date, i18n.language === 'es' ? "d 'de' MMMM 'de' yyyy" : 'MMMM d, yyyy', {
              locale: i18n.language === 'es' ? es : enUS
            })}
          </h2>

          {selectedDateTrainings.length === 0 ? (
            <Card>
              <p className="no-trainings">{t('noTrainingsToday')}</p>
            </Card>
          ) : (
            <div className="trainings-grid">
              {selectedDateTrainings.map((training) => (
                <Card
                  key={training.id}
                  onClick={() => navigate(`/training/${training.id}`)}
                  className="training-card"
                >
                  <h3 className="training-title">{training.title}</h3>
                  <p className="training-time">
                    {format(new Date(training.date), 'HH:mm')}
                  </p>
                  {training.user && (
                    <p className="training-student">
                      {training.user.name}
                    </p>
                  )}
                  {training.observations && (
                    <p className="training-observations">{training.observations}</p>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
